import {
  BLOCK_BORDER_RADIUS_PX,
  BLOCK_FILL,
  BLOCK_HOVERED_CELL_OPACITY,
  BLOCK_TRANSITION,
  FRONTIER_STROKE,
  type GetCellLogoTreatment,
  type GetTreemapCellStyle,
  type LabTransparencyVisualState,
} from "./powerTreemapStyle"

// Fade variant: instead of solid -> dotted dither -> dashed outline, the black
// square fades solid -> half-opacity fill (dashed outline visible, white glyph)
// -> empty (dashed outline still visible, glyph on a white badge).
const DEFAULT_STROKE_COLOR = "#303030"
const DEFAULT_DEAL_MEMBER_ICON_COLOR = "#111111"
const DEFAULT_CLOSED_ICON_COLOR = "#ffffff"

export type FadeTreemapStyleOptions = {
  backgroundColor?: string
  strokeColor?: string
  strokeWidth?: number
  iconColor?: string
  borderRadius?: number
  // Hover-aware treatment: while a country group is hovered, ITS cells take
  // this stroke (and hoveredRegionIconColor for their icons) while every
  // other cell keeps the normal treatment at FULL strength — no opacity
  // dimming of the non-hovered groups.
  hoveredRegionStrokeColor?: string
  hoveredRegionIconColor?: string
  // Fills the hovered group's cells with this color (e.g. solid red with
  // white icons) instead of only outlining them.
  hoveredRegionFillColor?: string
  // What the hoveredRegion* treatments highlight: the hovered cell's whole
  // country group ("region", the default) or ONE company at a time ("cell" —
  // only the exact cell under the pointer takes the hover stroke/fill/icon;
  // its region siblings keep the normal treatment, and nothing lightens).
  hoverScope?: "region" | "cell"
  // How the hovered country's red highlight keeps the FRONTIER distinction
  // legible — the plain region fill floods every cell the same red, so only
  // the icons hint at frontier access (Joanna 2026-07-12). Colors in
  // FADE_HOVER_FRONTIER_DISTINCTION_STYLE below.
  //   "hollow-others" — the country's frontier cells fill solid red; its
  //                     non-frontier cells go HOLLOW (red outline, no fill),
  //                     so the whole country reads red in two weights
  //   "cream-frontier-outline" — every cell fills red, but the frontier
  //                     cells keep a page-background outline punched through
  //                     the fill (the fill mode's icon vocabulary)
  hoveredRegionFrontierDistinction?: "hollow-others" | "cream-frontier-outline"
  // Outline for frontier-AI-development cells (the filled labs): a red border,
  // so the frontier compute reads without a loud fill. Defaults to the
  // FRONTIER_STROKE red, 0.5px heavier than the base strokeWidth of other cells.
  frontierStrokeColor?: string
  frontierStrokeWidth?: number
  // FILL mode for frontier cells (the 2026-07-12 exploration): each frontier
  // cell fills solid with this color, its border drops to the base
  // strokeWidth (same as non-frontier cells — no extra 0.5px) in the same
  // color as the fill, and its icon renders in frontierIconColor (the page
  // background, so the glyph punches out of the red). Overrides the
  // transparency-fade backgrounds on frontier cells.
  frontierFillColor?: string
  frontierIconColor?: string
}

// The hoveredRegionFrontierDistinction treatments' colors: the cream is the
// page background (the fill mode's punched-out icon color), used as the
// frontier cells' outline inside the hovered country's red mass.
export const FADE_HOVER_FRONTIER_DISTINCTION_STYLE = {
  creamOutlineColor: "#fffff8",
}

const withOpacity = (color: string, opacity: number): string => {
  if (color.startsWith("#")) {
    const hex = color.length === 4
      ? color.slice(1).split("").map(ch => ch + ch).join("")
      : color.slice(1)
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
  }
  return color
}

const getFadeBackgroundColor = (
  state: LabTransparencyVisualState,
  backgroundColor: string
): string => {
  if (state === "closed") return backgroundColor
  if (state === "dealMembers") return withOpacity(backgroundColor, 0.3)
  return "transparent"
}

// The dashed outline is visible the moment the fill starts fading, and stays
// visible through the fully-empty state. When strokeColor is customized, closed
// cells also get a solid outline so the stroke is visible on the default view.
const getFadeBorder = (
  state: LabTransparencyVisualState,
  strokeColor: string | undefined,
  strokeWidth: number,
): string => {
  const borderColor = strokeColor ?? DEFAULT_STROKE_COLOR
  const borderWidth = `${strokeWidth}px`
  if (state === "closed") {
    return strokeColor !== undefined ? `${borderWidth} solid ${borderColor}` : "none"
  }
  return `${borderWidth} dashed ${borderColor}`
}

export const createTreemapCellStyleFade = ({
  backgroundColor = BLOCK_FILL,
  strokeColor,
  strokeWidth = 1,
  borderRadius = BLOCK_BORDER_RADIUS_PX,
  hoveredRegionStrokeColor,
  hoveredRegionFillColor,
  hoverScope = "region",
  hoveredRegionFrontierDistinction,
  frontierStrokeColor = FRONTIER_STROKE,
  frontierStrokeWidth,
  frontierFillColor,
}: FadeTreemapStyleOptions = {}): GetTreemapCellStyle => ({
  left,
  top,
  width,
  height,
  opacity,
  state,
  isInHoveredRegion,
  isHoveredCell,
  isFrontier,
}) => {
  const hoverTreatmentActive =
    hoveredRegionStrokeColor !== undefined || hoveredRegionFillColor !== undefined
  // "cell" scope: the hover treatment marks ONE company at a time — only the
  // exact cell under the pointer, never its whole country group.
  const isHoverHighlighted =
    hoverScope === "cell" ? isHoveredCell : isInHoveredRegion
  return {
    left,
    top,
    width,
    height,
    backgroundColor:
      hoveredRegionFillColor !== undefined && isHoverHighlighted
        ? hoveredRegionFrontierDistinction === "hollow-others" && !isFrontier
          // "hollow-others": the hovered country's non-frontier cells keep
          // the red OUTLINE only — no fill — so frontier stays legible.
          ? "transparent"
          : hoveredRegionFillColor
        : isFrontier && frontierFillColor !== undefined
          ? frontierFillColor
          : getFadeBackgroundColor(state, backgroundColor),
    // A hovered region's red outline takes precedence; otherwise frontier
    // cells fill solid (frontierFillColor mode — border at the BASE width in
    // the fill's color, no extra 0.5px) or keep the normal fill with a
    // slightly heavier red border.
    border:
      hoveredRegionStrokeColor !== undefined && isHoverHighlighted
        ? hoveredRegionFrontierDistinction === "cream-frontier-outline" && isFrontier
          // "cream-frontier-outline": the frontier cells' outline punches
          // through the red fill in the page background color.
          ? `${strokeWidth}px solid ${FADE_HOVER_FRONTIER_DISTINCTION_STYLE.creamOutlineColor}`
          : getFadeBorder(state, hoveredRegionStrokeColor, strokeWidth)
        : isFrontier
          ? frontierFillColor !== undefined
            ? `${strokeWidth}px solid ${frontierFillColor}`
            : `${frontierStrokeWidth ?? strokeWidth + 0.5}px solid ${frontierStrokeColor}`
          : getFadeBorder(state, strokeColor, strokeWidth),
    borderRadius,
    boxSizing: "border-box",
    // In the hover treatments the red IS the highlight — the non-hovered
    // groups stay full black instead of dimming. Region scope lightens the
    // exact cell under the pointer so you can see which square the hover is
    // reading; cell scope doesn't need to (the red alone marks that cell).
    opacity: hoverTreatmentActive
      ? isHoveredCell && hoverScope === "region"
        ? BLOCK_HOVERED_CELL_OPACITY
        : 1
      : opacity,
    transition: BLOCK_TRANSITION,
  }
}

// Closed: white glyph on the solid black square. Faded mid-state: black glyph
// directly on the light half-opacity fill (no badge). Fully transparent:
// dark glyph on a white badge.
export const createCellLogoTreatmentFade = ({
  strokeColor,
  iconColor,
  hoveredRegionIconColor,
  hoverScope = "region",
  frontierStrokeColor = FRONTIER_STROKE,
  frontierIconColor,
}: Pick<
  FadeTreemapStyleOptions,
  "strokeColor" | "iconColor" | "hoveredRegionIconColor" | "hoverScope" | "frontierStrokeColor" | "frontierIconColor"
> = {}): GetCellLogoTreatment => (state, regionInfo) => {
  // See FadeTreemapStyleOptions.hoverScope — "cell" marks one company at a
  // time, so only the exact hovered cell's icon takes the hover color.
  const isHoverHighlighted =
    hoverScope === "cell" ? regionInfo?.isHoveredCell : regionInfo?.isInHoveredRegion
  // Hovered-region icon color wins; otherwise a frontier cell renders its
  // glyph in frontierIconColor (the fill mode's punched-out glyph) or, by
  // default, the same red as its outline so the logo reads as part of the
  // highlight.
  // Fill mode: the cell is solid red in every transparency state, so the
  // glyph always renders directly on it — never on the white badge.
  if (
    frontierIconColor !== undefined &&
    regionInfo?.isFrontier &&
    !(hoveredRegionIconColor !== undefined && isHoverHighlighted)
  ) {
    return { onWhiteBadge: false, color: frontierIconColor }
  }
  const resolvedIconColor =
    hoveredRegionIconColor !== undefined && isHoverHighlighted
      ? hoveredRegionIconColor
      : regionInfo?.isFrontier
        ? frontierIconColor ?? frontierStrokeColor
        : iconColor ?? strokeColor
  if (resolvedIconColor !== undefined) {
    if (state === "closed") return { onWhiteBadge: false, color: resolvedIconColor }
    if (state === "dealMembers") return { onWhiteBadge: false, color: resolvedIconColor }
    return { onWhiteBadge: true, color: resolvedIconColor }
  }
  if (state === "closed") return { onWhiteBadge: false, color: DEFAULT_CLOSED_ICON_COLOR }
  if (state === "dealMembers") {
    return { onWhiteBadge: false, color: DEFAULT_DEAL_MEMBER_ICON_COLOR }
  }
  return { onWhiteBadge: true, color: DEFAULT_DEAL_MEMBER_ICON_COLOR }
}

export const getTreemapCellStyleFade = createTreemapCellStyleFade()
export const getCellLogoTreatmentFade = createCellLogoTreatmentFade()
